import { z } from 'zod';
import { isoDaySchema } from './dates';
import { TREND_DIRECTIONS } from './kpi-history';
import { MEETING_LEVELS, MEETING_STATUSES } from './meetings';

// ---------------------------------------------------------------------------------------------
// Análise crítica: pauta automática e condução (PRD §11.25, Anexo C, RF-38, RN-28; DEC-050).

/** Fontes da pauta (DEC-050); MANUAL = item avulso incluído por quem prepara a reunião. */
export const AGENDA_ITEM_KINDS = [
  'KPI_CRITICAL',
  'OBJECTIVE_AT_RISK',
  'ACTION_LATE',
  'DEVIATION',
  'PENDING_DECISION',
  'CHANGE',
  'MANUAL',
] as const;
export type AgendaItemKindName = (typeof AGENDA_ITEM_KINDS)[number];
export const AGENDA_ITEM_KIND_LABELS: Record<AgendaItemKindName, string> = {
  KPI_CRITICAL: 'Indicador crítico',
  OBJECTIVE_AT_RISK: 'Objetivo em risco',
  ACTION_LATE: 'Ação atrasada',
  DEVIATION: 'Desvio informado',
  PENDING_DECISION: 'Decisão pendente',
  CHANGE: 'Mudança desde a última análise',
  MANUAL: 'Item incluído',
};

export const AGENDA_TITLE_MAX = 200;
export const AGENDA_DETAIL_MAX = 2000;

/** Retrato do assunto quando a pauta foi montada (para a condução e a ata — DEC-050). */
export const agendaSnapshotSchema = z.object({
  /** Mês do último dado ("AAAA-MM"). */
  period: z.string().nullable(),
  result: z.number().nullable(),
  target: z.number().nullable(),
  performance: z.number().nullable(),
  light: z.string().nullable(),
  /** Unidade para mostrar o resultado e a meta (ex.: "R$", "%"). */
  unit: z.string().nullable(),
  trend: z.object({ direction: z.enum(TREND_DIRECTIONS), favorable: z.boolean().nullable() }).nullable(),
});
export type AgendaSnapshot = z.infer<typeof agendaSnapshotSchema>;

const ref = z.object({ id: z.string().uuid(), name: z.string() });

export const DECISION_TEXT_MAX = 1000;
export const JUSTIFICATION_MAX = 4000;

/**
 * Decisão pendente: com ação, enquanto a ação não estiver concluída nem cancelada; sem ação, até ser cumprida — ou
 * até ser rejeitada na aprovação (DEC-053).
 */
export function decisionPending(d: {
  done: boolean;
  action: { status: string } | null;
  approvalStatus?: DecisionApprovalStatusName;
}): boolean {
  if (d.action) return !['DONE', 'CANCELLED'].includes(d.action.status);
  return !d.done && d.approvalStatus !== 'REJECTED';
}

// Aprovação de decisões (T-123a, RF-48; DEC-053).
export const DECISION_APPROVAL_STATUSES = ['NONE', 'PENDING', 'APPROVED', 'REJECTED'] as const;
export type DecisionApprovalStatusName = (typeof DECISION_APPROVAL_STATUSES)[number];
export const DECISION_APPROVAL_LABELS: Record<DecisionApprovalStatusName, string> = {
  NONE: 'Sem aprovação',
  PENDING: 'Aguardando aprovação',
  APPROVED: 'Aprovada',
  REJECTED: 'Rejeitada',
};
export const DECISION_VOTES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type DecisionVoteName = (typeof DECISION_VOTES)[number];
export const DECISION_VOTE_LABELS: Record<DecisionVoteName, string> = {
  PENDING: 'Aguardando',
  APPROVED: 'Aprovou',
  REJECTED: 'Rejeitou',
};
export const DECISION_APPROVERS_MAX = 10;
export const APPROVAL_COMMENT_MAX = 1000;

export const decisionApproverSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  vote: z.enum(DECISION_VOTES),
  comment: z.string().nullable(),
  decidedAt: z.string().nullable(),
  /** Rejeição de decisão com ação aberta: a ação foi cancelada (true) ou mantida (false). */
  actionCancelled: z.boolean().nullable(),
});
export type DecisionApprover = z.infer<typeof decisionApproverSchema>;

export const decisionSchema = z.object({
  id: z.string().uuid(),
  meetingId: z.string().uuid(),
  agendaItemId: z.string().uuid().nullable(),
  text: z.string(),
  responsible: ref.nullable(),
  /** Prazo ("AAAA-MM-DD"). */
  dueDate: z.string().nullable(),
  /** Cumprida (decisão sem ação). */
  done: z.boolean(),
  /** Ainda pendente (ação aberta ou decisão sem ação não cumprida). */
  pending: z.boolean(),
  action: z
    .object({ id: z.string().uuid(), title: z.string(), status: z.string(), planId: z.string().uuid() })
    .nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
  /** Aprovação (DEC-053): estado e votos. */
  approval: z.object({
    status: z.enum(DECISION_APPROVAL_STATUSES),
    decidedAt: z.string().nullable(),
    approvers: z.array(decisionApproverSchema),
  }),
});
export type Decision = z.infer<typeof decisionSchema>;

export const agendaItemSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(AGENDA_ITEM_KINDS),
  position: z.number().int(),
  title: z.string(),
  detail: z.string().nullable(),
  kpi: ref.nullable(),
  objective: ref.nullable(),
  action: z.object({ id: z.string().uuid(), name: z.string(), planId: z.string().uuid() }).nullable(),
  alertId: z.string().uuid().nullable(),
  decisionId: z.string().uuid().nullable(),
  department: ref.nullable(),
  businessUnit: ref.nullable(),
  snapshot: agendaSnapshotSchema.nullable(),
  /** Item automático (montado pelo sistema) ou avulso. */
  automatic: z.boolean(),
  createdByName: z.string().nullable(),
  /** Condução (T-121b): justificativa, discutido e as decisões do item. */
  justification: z.string().nullable(),
  discussed: z.boolean(),
  discussedAt: z.string().nullable(),
  discussedByName: z.string().nullable(),
  decisions: z.array(decisionSchema),
  /** Item "decisão pendente": a decisão da análise anterior como está agora (cumprir pela rota da decisão). */
  pendingDecision: decisionSchema.nullable(),
  /** Resultado, meta e tendência de agora (dados reais), com a reunião em andamento ou encerrada. */
  current: agendaSnapshotSchema.nullable(),
});
export type AgendaItem = z.infer<typeof agendaItemSchema>;

export const agendaSchema = z.object({
  items: z.array(agendaItemSchema),
  /** Quando a pauta foi montada pela última vez (null: ainda não montada). */
  generatedAt: z.string().nullable(),
  /** Itens automáticos retirados por quem prepara a reunião (não voltam ao atualizar). */
  dismissed: z.number().int(),
  /** Itens da pauta fora do escopo de quem pede (não aparecem para essa pessoa — DEC-016). */
  hidden: z.number().int(),
  /** Quem pede pode montar e mudar a pauta (análise crítica agendada, no escopo, com "editar"). */
  canEdit: z.boolean(),
  /** Quem pede pode conduzir (análise crítica em andamento, no escopo, com "editar"). */
  canConduct: z.boolean(),
  /** Última análise encerrada do mesmo nível (base das mudanças), se houver. */
  previous: z.object({ id: z.string().uuid(), title: z.string(), startsAt: z.string() }).nullable(),
});
export type Agenda = z.infer<typeof agendaSchema>;

/** Item avulso: título, detalhe opcional e um assunto opcional (KPI, objetivo ou ação do projeto). */
export const agendaItemInputSchema = z
  .object({
    title: z
      .string({ message: 'Informe o assunto do item.' })
      .trim()
      .min(3, 'Informe o assunto do item (pelo menos 3 caracteres).')
      .max(AGENDA_TITLE_MAX, 'Assunto longo demais.'),
    detail: z
      .string()
      .trim()
      .max(AGENDA_DETAIL_MAX, `O detalhe pode ter no máximo ${AGENDA_DETAIL_MAX} caracteres.`)
      .nullable()
      .optional()
      .transform((v) => v || null),
    kpiId: z
      .string()
      .uuid('Indicador inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    objectiveId: z
      .string()
      .uuid('Objetivo inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    actionId: z
      .string()
      .uuid('Ação inválida.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
  })
  .refine((v) => [v.kpiId, v.objectiveId, v.actionId].filter(Boolean).length <= 1, {
    message: 'Vincule no máximo um assunto ao item (indicador, objetivo ou ação).',
    path: ['kpiId'],
  });
export type AgendaItemInput = z.input<typeof agendaItemInputSchema>;

/** Assuntos que podem ser vinculados a um item avulso (no escopo de quem prepara) e os possíveis responsáveis. */
export const agendaOptionsSchema = z.object({
  kpis: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string() })),
  objectives: z.array(ref),
  actions: z.array(z.object({ id: z.string().uuid(), name: z.string(), planName: z.string() })),
  /** Pessoas ativas da empresa que podem ser responsáveis por uma decisão (e pela ação dela). */
  people: z.array(ref),
  /** Quem pode aprovar decisões: pessoas ativas com "aprovar" em Reuniões e Decisões (DEC-053). */
  approvers: z.array(ref),
});

/** Condução do item: justificativa e discutido. */
export const conductInputSchema = z.object({
  justification: z
    .string()
    .trim()
    .max(JUSTIFICATION_MAX, `A justificativa pode ter no máximo ${JUSTIFICATION_MAX} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null),
  discussed: z.boolean({ message: 'Informe se o item foi discutido.' }),
});
export type ConductInput = z.input<typeof conductInputSchema>;

/** Aprovadores da decisão (DEC-053): até 10 pessoas, sem repetir. */
const approverIds = z
  .array(z.string().uuid('Aprovador inválido.'))
  .max(DECISION_APPROVERS_MAX, `No máximo ${DECISION_APPROVERS_MAX} aprovadores.`)
  .transform((ids) => [...new Set(ids)]);

const isoDayOrNull = isoDaySchema
  .nullable()
  .optional()
  .transform((v) => v ?? null);

/** Decisão sobre um item: texto, responsável e prazo; "criar ação" exige responsável e prazo (RN-14). */
export const decisionInputSchema = z
  .object({
    agendaItemId: z.string({ message: 'Escolha o item da pauta.' }).uuid('Escolha o item da pauta.'),
    text: z
      .string({ message: 'Escreva a decisão.' })
      .trim()
      .min(3, 'Escreva a decisão (pelo menos 3 caracteres).')
      .max(DECISION_TEXT_MAX, 'Decisão longa demais.'),
    responsibleId: z
      .string()
      .uuid('Responsável inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    dueDate: isoDayOrNull,
    createAction: z.boolean().default(false),
    approverIds: approverIds.default([]),
  })
  .superRefine((v, ctx) => {
    if (!v.createAction) return;
    if (!v.responsibleId) {
      ctx.addIssue({
        code: 'custom',
        path: ['responsibleId'],
        message: 'Para criar a ação, escolha o responsável.',
      });
    }
    if (!v.dueDate) {
      ctx.addIssue({ code: 'custom', path: ['dueDate'], message: 'Para criar a ação, informe o prazo.' });
    }
  });
export type DecisionInput = z.input<typeof decisionInputSchema>;

/** Editar a decisão (sem ação criada). */
export const decisionUpdateSchema = z.object({
  text: z
    .string({ message: 'Escreva a decisão.' })
    .trim()
    .min(3, 'Escreva a decisão (pelo menos 3 caracteres).')
    .max(DECISION_TEXT_MAX, 'Decisão longa demais.'),
  responsibleId: z
    .string()
    .uuid('Responsável inválido.')
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  dueDate: isoDayOrNull,
  /** Troca os aprovadores (só antes do primeiro voto); ausente = mantém. */
  approverIds: approverIds.optional(),
});
export type DecisionUpdate = z.input<typeof decisionUpdateSchema>;

/** Voto do aprovador: rejeitar exige comentário; com a ação da decisão aberta, dizer se ela é cancelada. */
export const decisionVoteSchema = z
  .object({
    approve: z.boolean({ message: 'Escolha aprovar ou rejeitar.' }),
    comment: z
      .string()
      .trim()
      .max(APPROVAL_COMMENT_MAX, `O comentário pode ter no máximo ${APPROVAL_COMMENT_MAX} caracteres.`)
      .nullable()
      .optional()
      .transform((v) => v || null),
    cancelAction: z
      .boolean()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
  })
  .superRefine((v, ctx) => {
    if (!v.approve && (!v.comment || v.comment.length < 3)) {
      ctx.addIssue({
        code: 'custom',
        path: ['comment'],
        message: 'Para rejeitar, explique o motivo (pelo menos 3 caracteres).',
      });
    }
    if (v.approve && v.cancelAction !== null) {
      ctx.addIssue({ code: 'custom', path: ['cancelAction'], message: 'Cancelar a ação só na rejeição.' });
    }
  });
export type DecisionVoteInput = z.input<typeof decisionVoteSchema>;

/** Minhas aprovações (T-123a): as decisões em que a pessoa é aprovadora, com o contexto para decidir. */
export const decisionApprovalListQuerySchema = z.object({
  status: z.enum(['pending', 'decided']).default('pending'),
  /** Cursor: "criadoEm|id" do pedido de aprovação do último item da página anterior. */
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
/** Uma aprovação da pessoa: a decisão com o contexto para decidir (reunião, item, responsável, prazo, ação, votos). */
export const decisionApprovalItemSchema = z.object({
  decision: decisionSchema,
  project: ref,
  meeting: z.object({
    id: z.string().uuid(),
    title: z.string(),
    startsAt: z.string(),
    status: z.string(),
  }),
  /** O item da pauta: por que se decidiu. */
  item: z.object({ title: z.string(), kind: z.enum(AGENDA_ITEM_KINDS) }).nullable(),
  /** O meu voto. */
  mine: decisionApproverSchema,
});
export type DecisionApprovalItem = z.infer<typeof decisionApprovalItemSchema>;
export const decisionApprovalListSchema = z.object({
  items: z.array(decisionApprovalItemSchema),
  nextCursor: z.string().nullable(),
});
export type DecisionApprovalList = z.infer<typeof decisionApprovalListSchema>;

export const decisionDoneSchema = z.object({
  done: z.boolean({ message: 'Informe se a decisão foi cumprida.' }),
});

/** Aba "Análises críticas" do objetivo: itens discutidos ligados a ele (direto ou por KPI dele), com as decisões. */
export const objectiveAnalysesSchema = z.object({
  items: z.array(
    z.object({
      meeting: z.object({
        id: z.string().uuid(),
        title: z.string(),
        startsAt: z.string(),
        level: z.enum(MEETING_LEVELS),
        status: z.enum(MEETING_STATUSES),
      }),
      item: z.object({
        id: z.string().uuid(),
        kind: z.enum(AGENDA_ITEM_KINDS),
        title: z.string(),
        justification: z.string().nullable(),
        snapshot: agendaSnapshotSchema.nullable(),
        discussedAt: z.string().nullable(),
        kpi: ref.nullable(),
      }),
      decisions: z.array(decisionSchema),
    }),
  ),
});
export type ObjectiveAnalyses = z.infer<typeof objectiveAnalysesSchema>;
export type AgendaOptions = z.infer<typeof agendaOptionsSchema>;
